import { MetaPreviewData } from '../../../../../models/meta-template/preview-interfaces/meta-preview-data.interface';

/** A piece of copy cut down to what a placement shows before its "See more". */
export interface TruncatedCopy {
  text: string;
  /** True when something was cut, so the placement should show its "See more". */
  truncated: boolean;
}

/**
 * Copy shared by every Meta placement: the fixed page identity, the
 * placeholders shown while a field is still empty, and the truncation rules.
 *
 * The template has no page field — the account type only says Facebook or
 * Instagram — so the page name and avatar are fixed placeholders, matching the
 * reference screenshots.
 */
export class MetaPreviewCopy {
  static readonly FACEBOOK_PAGE_NAME = 'Valamar';
  static readonly INSTAGRAM_ACCOUNT_NAME = 'valamarhotels';
  static readonly AVATAR = 'assets/images/meta-preview/valamar-avatar.png';

  static headline(data: MetaPreviewData): string {
    return data.headlines[0] || 'Your headline appears here';
  }

  static primaryText(data: MetaPreviewData): string {
    return data.primaryTexts[0] || 'Your primary text appears here';
  }

  static cta(data: MetaPreviewData): string {
    return data.ctaText || 'Call to action';
  }

  /** The landing page's bare host (`valamar.com`), or a placeholder while there is none. */
  static domain(data: MetaPreviewData): string {
    if (!data.url) {
      return 'your-website.com';
    }
    try {
      return new URL(data.url).hostname.replace(/^www\./i, '');
    } catch {
      return data.url;
    }
  }

  /**
   * Facebook feed rule: the first paragraph only, cut on a word boundary at
   * `maxChars`. Anything left over is behind "See more".
   */
  static firstParagraph(text: string, maxChars: number): TruncatedCopy {
    const [first, ...rest] = text.split(/\r?\n/);
    const hasMoreParagraphs = rest.some((line) => line.trim() !== '');
    if (first.length <= maxChars) {
      return { text: first, truncated: hasMoreParagraphs };
    }
    const cut = first.slice(0, maxChars);
    const lastSpace = cut.lastIndexOf(' ');
    return { text: (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd(), truncated: true };
  }

  /**
   * Instagram caption rule: the caption, username included, is cut mid-word
   * at `maxChars` characters, which is what fills two lines before "... more".
   */
  static caption(username: string, text: string, maxChars: number): TruncatedCopy {
    return this.cut(text, maxChars - username.length - 1);
  }

  /** The whole text on one line, cut mid-word at `maxChars` characters. */
  static cut(text: string, maxChars: number): TruncatedCopy {
    const flat = text.replace(/\s*\r?\n\s*/g, ' ');
    if (flat.length <= maxChars) {
      return { text: flat, truncated: false };
    }
    return { text: flat.slice(0, maxChars), truncated: true };
  }

  /** The whole text with its paragraphs joined, for placements that clamp it with CSS. */
  static flat(text: string): string {
    return text.replace(/\s*\r?\n\s*/g, ' ');
  }
}
